/**
 * «Cambió el plano» para DWG/DXF: lee del servidor lo que hace falta para comparar las dos revisiones de un dibujo.
 * A diferencia de IFC (se mide en el navegador), un DWG lo lee el SERVIDOR (el sidecar): aquí solo se espera a que
 * termine de leer la revisión nueva y se piden los dos inventarios con lo que los acompaña (clasificación de capas,
 * correcciones de la persona, unidad elegida, altura ya propuesta y altos de vanos). Nada de esto llama a la IA.
 */
import type { PlanoAlturaPropuesta } from '@supernova/shared';
import { getAltosVanos, getPlano } from '../api/planos.js';
import type { DatosDePlanoDwg } from './compararRevisionDwg.js';

export interface PlanosDeLaRevisionDwg {
  /** El plano con el que se armó la hoja. */
  a: DatosDePlanoDwg & { alturaProp: PlanoAlturaPropuesta | null };
  /** La revisión nueva. */
  b: DatosDePlanoDwg;
  /** Los altos de los tipos de vano de la hoja, como los escribe la pantalla (texto). */
  altosVanos: Record<string, string>;
}

const ESPERA_MS = 3000;
const ESPERA_MAX_MS = 10 * 60 * 1000;

const dormir = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolver, rechazar) => {
    if (signal?.aborted) {
      rechazar(new DOMException('cancelado', 'AbortError'));
      return;
    }
    const alAbortar = () => {
      clearTimeout(t);
      rechazar(new DOMException('cancelado', 'AbortError'));
    };
    const t = setTimeout(() => {
      signal?.removeEventListener('abort', alAbortar);
      resolver();
    }, ms);
    signal?.addEventListener('abort', alAbortar, { once: true });
  });

/**
 * Lee los dos planos de la revisión. `esperar` (por omisión sí): si el servidor todavía está leyendo la revisión nueva,
 * se pregunta cada pocos segundos hasta que termine (o falle, o pasen 10 minutos). Con `esperar: false` se lee lo que
 * haya y, si no está lista, se falla de inmediato (para recalcular algo de una comparación ya guardada).
 */
export async function leerPlanosDeLaRevisionDwg(
  projectId: string,
  fileAnteriorId: string,
  fileNuevoId: string,
  o: { signal?: AbortSignal; esperar?: boolean } = {},
): Promise<PlanosDeLaRevisionDwg> {
  const esperar = o.esperar !== false;
  const desde = Date.now();
  let b = await getPlano(projectId, fileNuevoId);
  while (b.status !== 'ready' || !b.inventario) {
    if (o.signal?.aborted) throw new DOMException('cancelado', 'AbortError');
    if (b.status === 'failed') throw new Error(b.error || 'No se pudo leer la revisión nueva en el servidor: descártala y vuelve a subirla.');
    if (!esperar) throw new Error('La revisión nueva todavía no está leída.');
    if (Date.now() - desde > ESPERA_MAX_MS) throw new Error('La revisión nueva tarda demasiado en leerse: vuelve a intentarlo en unos minutos.');
    await dormir(ESPERA_MS, o.signal);
    b = await getPlano(projectId, fileNuevoId);
  }
  const [a, altos] = await Promise.all([getPlano(projectId, fileAnteriorId), getAltosVanos(projectId, fileAnteriorId)]);
  return {
    a: {
      inventario: a.inventario,
      capasClasificadas: a.capasClasificadas ?? null,
      decisionOverride: a.decisionesCapa ?? {},
      elegida: a.escalaElegida ?? null,
      alturaProp: a.alturaPropuesta ?? null,
    },
    b: {
      inventario: b.inventario,
      capasClasificadas: b.capasClasificadas ?? null,
      decisionOverride: b.decisionesCapa ?? {},
      elegida: b.escalaElegida ?? null,
    },
    altosVanos: Object.fromEntries(Object.entries(altos).map(([k, v]) => [k, String(v)])),
  };
}
